import * as React from "react";
import { Tabs, Card } from "@mantine/core";
import { TabGroup, TabGroupTab } from "@bi-studio/shared/lib";

interface TabGroupViewProps {
    tabGroup: TabGroup;
}

export function TabGroupView({ tabGroup }: TabGroupViewProps) {
    const sortedTabs = [...(tabGroup.tabs || [])].sort((a, b) => a.order - b.order);
    const [activeTab, setActiveTab] = React.useState<string | null>(
        sortedTabs.length > 0 ? sortedTabs[0].id : null
    );
    const [loadedTabs, setLoadedTabs] = React.useState<Set<string>>(
        new Set(sortedTabs.length > 0 ? [sortedTabs[0].id] : [])
    );

    // If active tab doesn't exist (e.g. data changed), reset to first
    React.useEffect(() => {
        if (sortedTabs.length > 0 && (!activeTab || !sortedTabs.find(t => t.id === activeTab))) {
            const firstId = sortedTabs[0].id;
            setActiveTab(firstId);
            setLoadedTabs(prev => new Set(prev).add(firstId));
        } else if (activeTab) {
            setLoadedTabs(prev => new Set(prev).add(activeTab));
        }
    }, [sortedTabs, activeTab]);

    if (!tabGroup.tabs || tabGroup.tabs.length === 0) {
        return (
            <div className="space-y-4">
                <div className="p-8 text-center text-muted-foreground">
                    This tab group has no tabs.
                </div>
            </div>
        );
    }

    const renderContent = (tab: TabGroupTab) => {
        if (tab.type === "report" && tab.reportId) {
            return (
                <iframe
                    src={`/reports/${tab.reportId}?embed=true`}
                    className="w-full h-[calc(100vh-200px)] border-0"
                    title={tab.title}
                />
            );
        }
        if (tab.type === "chart" && tab.chartId) {
            return (
                <iframe
                    src={`/charts/${tab.chartId}?embed=true`}
                    className="w-full h-[calc(100vh-200px)] border-0"
                    title={tab.title}
                />
            );
        }
        if (tab.type === "dashboard" && tab.dashboardId) {
            return (
                <iframe
                    src={`/dashboards/${tab.dashboardId}?embed=true`}
                    className="w-full h-[calc(100vh-200px)] border-0"
                    title={tab.title}
                />
            );
        }
        if (tab.type === "form" && tab.formId) {
            return (
                <iframe
                    src={`/forms/${tab.formId}?embed=true`}
                    className="w-full h-[calc(100vh-200px)] border-0"
                    title={tab.title}
                />
            );
        }

        return <div>Unknown content</div>;
    };

    return (
        <div className="space-y-4">
            <Tabs
                value={activeTab}
                onChange={setActiveTab}
                variant="outline"
                radius="md"
                classNames={{
                    list: 'hide-scrollbar'
                }}
                styles={{
                    list: {
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                    },
                    tab: {
                        flexShrink: 0
                    }
                }}
            >
                <Tabs.List>
                    {sortedTabs.map((tab) => (
                        <Tabs.Tab key={tab.id} value={tab.id}>
                            {tab.title}
                        </Tabs.Tab>
                    ))}
                </Tabs.List>

                {sortedTabs.map((tab) => (
                    <Tabs.Panel key={tab.id} value={tab.id} pt="xs" keepMounted={true}>
                        <Card withBorder padding={0} radius="sm" className="overflow-hidden">
                            {/* Using iframe approach for now to ensure isolation and reuse existing pages */}
                            {/* We need to ensure authentication works in iframes (same origin) */}
                            {loadedTabs.has(tab.id) ? (
                                renderContent(tab)
                            ) : (
                                <div className="w-full h-[calc(100vh-200px)] flex items-center justify-center text-muted-foreground border-0">
                                    Loading tab...
                                </div>
                            )}
                        </Card>
                    </Tabs.Panel>
                ))}
            </Tabs>
        </div>
    );
}
